import { AnalyticsUserEvent } from '@fruitful/types';
import { router } from 'expo-router';
import { useEffect, useMemo, useState } from 'react';
import {
    FullScreenSpinner,
    ScreenContainer,
    useToastController,
} from 'smoothie';

import { assertNotNullish } from '../../../common/misc';
import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import { sortBankAccounts } from '../../../common/utils/bankingUtils';
import { format, ISO_DATE_ONLY_FORMAT } from '../../../common/utils/dateUtils';
import { track, trackView } from '../../../logger/Logger';
import { useMoneyMapWorkflow } from '../../../roadmap/hooks/useMoneyMapWorkflow';
import {
    useAddExistingBeneficiary,
    useCreateAndAddBeneficiary,
} from '../../hooks/beneficiaries/useAddBeneficiary';
import { useGetAllAccountsWithBeneficiaries } from '../../hooks/beneficiaries/useGetAccountsWithBeneficiaries';
import { useGetBeneficiaries } from '../../hooks/beneficiaries/useGetBeneficiaries';
import { RelationshipStatus } from '../../types/BankAccount';
import {
    AddBeneficiaryForm,
    type BeneficiaryFormValues,
} from './AddBeneficiaryForm';
import { ChooseBeneficiary } from './ChooseBeneficiary';
import { Overview } from './Overview';
import {
    ReviewBeneficiary,
    type ReviewBeneficiaryAccount,
} from './ReviewBeneficiary';
import { SelectBeneficiaryAccounts } from './SelectBeneficiaryAccounts';

export interface NewBeneficiary extends BeneficiaryFormValues {
    bankAccountIds: string[];
}

export interface ExistingBeneficiary extends NewBeneficiary {
    beneficiaryId: string;
}

export const Beneficiaries = () => {
    type steps =
        | 'overview'
        | 'selectAccounts'
        | 'selectExisting'
        | 'addNew'
        | 'reviewNew'
        | 'reviewExisting';

    const toast = useToastController();
    const [step, setStep] = useState<steps>('overview');
    const [prevStep, setPrevStep] = useState<steps | null>(null);

    const [pendingBeneficiary, setPendingBeneficiary] = useState<
        Partial<ExistingBeneficiary> | Partial<NewBeneficiary> | null
    >(null);

    const user = useAuthedUser();
    const {
        data: allDetails,
        isLoading: isLoadingAccounts,
        refetch: refetchAccounts,
        isRefetching: isRefetchingAccounts,
    } = useGetAllAccountsWithBeneficiaries({});

    const {
        data: beneficiaries,
        isLoading: isLoadingBeneficiaries,
        refetch: refetchBeneficiaries,
        isRefetching: isRefetchingBeneficiaries,
    } = useGetBeneficiaries({});

    const { moneyMap, isLoadingMoneyMap } = useMoneyMapWorkflow();

    const sortedBankAccountDetails = sortBankAccounts(
        allDetails?.bankAccountDetails ?? [],
        moneyMap,
    );

    const { mutateAsync: addNewBeneficiary, isPending: isAddingNew } =
        useCreateAndAddBeneficiary();
    const { mutateAsync: addExistingBeneficiary, isPending: isAddingExisting } =
        useAddExistingBeneficiary();

    const isJointMembership = user.partnerMember != null;

    useEffect(() => {
        trackView('Banking/Beneficiaries');
    }, []);

    const selectAccounts =
        allDetails?.bankAccountDetails.map((d) => ({
            ...d,
            id: d.bankAccountId,
            nickname: d.nickname ?? 'Cash account',
            icon: d.icon ?? 'fruitful',
            color: d.color ?? 'green',
            isJointAccount:
                d.jointUserRelationshipStatus === RelationshipStatus.ACTIVE,
            beneficiaryCount: d.beneficiaries.length,
        })) ?? [];

    const onAddConfirm = async () => {
        const dateOfBirth = `${format(assertNotNullish(pendingBeneficiary?.dateOfBirth), ISO_DATE_ONLY_FORMAT)}T00:00:00Z`;
        const newBeneficiary = {
            ...pendingBeneficiary,
            dateOfBirth,
        };
        await addNewBeneficiary(
            {
                body: newBeneficiary as NewBeneficiary,
            },
            {
                onError: () => {
                    toast.show('Could not add beneficiary');
                    setStep('overview');
                },
                onSuccess: () => {
                    toast.show('Beneficiary added');
                    track(AnalyticsUserEvent.BENEFICIARY_ADDED, {
                        type: 'new',
                    });
                    setStep('overview');
                },
                onSettled: async () => {
                    await refetchAccounts();
                    await refetchBeneficiaries();
                },
            },
        );
    };

    const onReviewConfirm = async () => {
        const { beneficiaryId } = pendingBeneficiary as ExistingBeneficiary;
        await addExistingBeneficiary(
            {
                body: {
                    beneficiaryId,
                    bankAccountIds: pendingBeneficiary?.bankAccountIds ?? [],
                },
            },
            {
                onError: () => {
                    toast.show('Could not add beneficiary');
                    setStep('overview');
                },
                onSuccess: () => {
                    toast.show('Beneficiary added');
                    track(AnalyticsUserEvent.BENEFICIARY_ADDED, {
                        type: 'existing',
                    });
                    setStep('overview');
                },
                onSettled: async () => {
                    await refetchAccounts();
                    await refetchBeneficiaries();
                },
            },
        );
    };

    const widget = useMemo(() => {
        switch (step) {
            case 'overview':
                setPrevStep(null);
                return (
                    <Overview
                        user={user}
                        bankAccountDetails={sortedBankAccountDetails}
                        isJointMembership={isJointMembership}
                        onPress={() => {
                            setStep('selectAccounts');
                        }}
                        onUpdate={async () => {
                            await refetchAccounts();
                            await refetchBeneficiaries();
                        }}
                    />
                );
            case 'selectAccounts':
                setPrevStep('overview');
                return (
                    <SelectBeneficiaryAccounts
                        accounts={selectAccounts}
                        onContinue={(selectedAccountIds: string[]) => {
                            setPendingBeneficiary({
                                bankAccountIds: selectedAccountIds,
                            });
                            if ((beneficiaries ?? []).length > 0) {
                                setStep('selectExisting');
                            } else {
                                setStep('addNew');
                            }
                        }}
                    />
                );
            case 'selectExisting':
                setPrevStep('selectAccounts');
                return (
                    <ChooseBeneficiary
                        existingBeneficiaries={
                            beneficiaries?.map((b) => ({
                                id: b.id,
                                name: `${b.legalFirstName} ${b.legalLastName}`,
                            })) ?? []
                        }
                        onAddNew={() => {
                            setStep('addNew');
                        }}
                        onSelectExisting={(id: string) => {
                            const existing = beneficiaries?.find(
                                (b) => b.id === id,
                            );
                            setPendingBeneficiary((prev) => {
                                return {
                                    beneficiaryId: id,
                                    ...existing,
                                    bankAccountIds: prev?.bankAccountIds,
                                };
                            });
                            setStep('reviewExisting');
                        }}
                    />
                );
            case 'addNew':
                setPrevStep('selectAccounts');
                return (
                    <AddBeneficiaryForm
                        onContinue={(data: BeneficiaryFormValues) => {
                            const updated: Partial<NewBeneficiary> = {
                                ...data,
                            };
                            setPendingBeneficiary((prevState) => {
                                return {
                                    ...updated,
                                    bankAccountIds: prevState?.bankAccountIds,
                                };
                            });
                            setStep('reviewNew');
                        }}
                    />
                );
            case 'reviewNew':
                setPrevStep('addNew');
                return (
                    <ReviewBeneficiary
                        data={pendingBeneficiary}
                        accounts={
                            (pendingBeneficiary?.bankAccountIds?.map((b) => {
                                return selectAccounts.find(
                                    (a) => a.bankAccountId === b,
                                );
                            }) as ReviewBeneficiaryAccount[]) ?? []
                        }
                        onConfirm={onAddConfirm}
                    />
                );
            case 'reviewExisting':
                setPrevStep('selectExisting');
                return (
                    <ReviewBeneficiary
                        data={pendingBeneficiary}
                        accounts={
                            (pendingBeneficiary?.bankAccountIds?.map((b) => {
                                return selectAccounts.find(
                                    (a) => a.bankAccountId === b,
                                );
                            }) as ReviewBeneficiaryAccount[]) ?? []
                        }
                        onConfirm={onReviewConfirm}
                    />
                );
        }
    }, [step, beneficiaries, allDetails, pendingBeneficiary]);

    if (
        isLoadingAccounts ||
        isLoadingBeneficiaries ||
        isAddingNew ||
        isAddingExisting ||
        isRefetchingBeneficiaries ||
        isRefetchingAccounts ||
        isLoadingMoneyMap
    ) {
        return <FullScreenSpinner />;
    }

    return (
        <ScreenContainer
            key={step}
            title="Beneficiaries"
            hasBackButton={true}
            onBack={() => {
                if (prevStep !== null) {
                    setStep(prevStep);
                } else {
                    if (!router.canGoBack()) {
                        router.navigate('/home/banking');
                    } else {
                        router.back();
                    }
                }
            }}
        >
            {widget}
        </ScreenContainer>
    );
};
